import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
// UI
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
// Styles
import styles from './MobileSelectSheet.module.scss';

export type SheetOption = Record<string, any>;

type MobileSelectSheetProps = {
  open: boolean;
  title: string;
  options: SheetOption[];
  getOptionLabel?: (option: SheetOption) => string;
  getOptionValue?: (option: SheetOption) => string;
  selectedValue?: string | null;
  onSelect: (option: SheetOption) => void;
  onClose: () => void;
  /** When set, a search input is shown and typing calls this (e.g. async lookups). */
  onSearch?: (query: string) => void;
  searchPlaceholder?: string;
  loading?: boolean;
  emptyText?: string;
};

const defaultGetLabel = (option: SheetOption) => String(option.label);
const defaultGetValue = (option: SheetOption) => String(option.value);

/**
 * Full screen picker for mobile: optional search input with the options listed below.
 * Sized to the visual viewport so the on-screen keyboard never covers the list.
 */
const MobileSelectSheet = ({
  open,
  title,
  options,
  getOptionLabel = defaultGetLabel,
  getOptionValue = defaultGetValue,
  selectedValue,
  onSelect,
  onClose,
  onSearch,
  searchPlaceholder = 'Search',
  loading = false,
  emptyText = 'No results',
}: MobileSelectSheetProps) => {
  const [query, setQuery] = useState('');
  const [viewport, setViewport] = useState<{ top: number; height: number }>();
  const selectedRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isSearchable = Boolean(onSearch);

  // Follow the visible area while the keyboard is open.
  useEffect(() => {
    if (!open) {
      return;
    }

    const visualViewport = window.visualViewport;

    if (!visualViewport) {
      return;
    }

    const update = () =>
      setViewport({
        top: visualViewport.offsetTop,
        height: visualViewport.height,
      });

    update();
    visualViewport.addEventListener('resize', update);
    visualViewport.addEventListener('scroll', update);

    return () => {
      visualViewport.removeEventListener('resize', update);
      visualViewport.removeEventListener('scroll', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery('');
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  // The modal underneath locks body scroll (body-scroll-lock), which cancels touchmove on
  // everything outside the modal, including this portalled sheet. Stopping the event
  // before it reaches `document` lets the list scroll on iOS.
  useEffect(() => {
    const list = listRef.current;

    if (!open || !list) {
      return;
    }

    const stopPropagation = (e: TouchEvent) => e.stopPropagation();

    list.addEventListener('touchmove', stopPropagation, { passive: true });

    return () => list.removeEventListener('touchmove', stopPropagation);
  }, [open]);

  useEffect(() => {
    if (open) {
      selectedRef.current?.scrollIntoView({ block: 'center' });
    }
  }, [open]);

  if (!open || typeof document === 'undefined') {
    return null;
  }

  const handleQueryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuery(e.target.value);
    onSearch?.(e.target.value);
  };

  const showEmpty =
    !loading && options.length === 0 && (!isSearchable || query);

  return createPortal(
    <div
      className={styles.sheet}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={
        viewport
          ? { top: viewport.top, height: viewport.height, bottom: 'auto' }
          : undefined
      }
    >
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close"
        >
          <MillionsIcon name="close" size={24} />
        </button>
      </div>

      {isSearchable && (
        <div className={styles.searchWrapper}>
          <MillionsIcon name="search" className={styles.searchIcon} />
          <input
            // 16px font keeps iOS from zooming on focus (see scss)
            className={styles.searchInput}
            type="text"
            value={query}
            onChange={handleQueryChange}
            placeholder={searchPlaceholder}
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint="search"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
        </div>
      )}

      <div className={styles.list} role="listbox" ref={listRef}>
        {options.map((option) => {
          const value = getOptionValue(option);
          const isSelected = value === selectedValue;

          return (
            <button
              key={value}
              ref={isSelected ? selectedRef : undefined}
              type="button"
              role="option"
              aria-selected={isSelected}
              className={cn(styles.option, { [styles.selected]: isSelected })}
              onClick={() => onSelect(option)}
            >
              {getOptionLabel(option)}
            </button>
          );
        })}
        {loading && <div className={styles.status}>Loading...</div>}
        {showEmpty && <div className={styles.status}>{emptyText}</div>}
      </div>
    </div>,
    document.body
  );
};

export default MobileSelectSheet;
